/* Native catalog membership state.
 *
 * Purpose: keep the authenticated Bank state consistent on the routes that do
 * not render the Pup Club portal (collection, search, native product, cart page)
 * so the theme does not show guest state to a signed-in member, and does not
 * flash member pricing to anyone else.
 *
 * Contract:
 *  - Entitlement comes only from PKPupClub.accountState(), the authenticated
 *    backend/session contract. No localStorage value, URL parameter, cart
 *    attribute, CSS class or page visit may grant a benefit.
 *  - Any error, timeout, unknown status or missing client fails CLOSED: the
 *    standard price stays primary and no member discount is shown.
 *  - On portal routes pk-vip-portal.js owns window.PK_PUP_CLUB_AUTH. This file
 *    then only listens and paints, so the two never race for the same global.
 *  - Meta (view=ad) is never a member surface and is skipped outright.
 */
(() => {
  'use strict';

  const html = document.documentElement;
  if (html.dataset.pkEntry === 'ad') return;

  // Once a shopper opts out, no checkout may submit the old membership cart.
  // A failed/partial removal stays blocked until a later retry verifies absence.
  ['click', 'submit'].forEach((type) => document.addEventListener(type, (event) => {
    if (window.PK_BANK_OPTOUT_BLOCKED !== true) return;
    const checkout = type === 'submit'
      ? event.submitter?.name === 'checkout' || event.target.querySelector('[name="checkout"]')
      : event.target.closest('[name="checkout"]');
    if (!checkout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true));

  // Bank-only correction. Leave the shared Meta drawer asset unchanged. A shopper
  // can have multiple Club lines with different properties: opting out removes
  // all of them and only reports success after the cart confirms their absence.
  customElements.whenDefined('pm-cart-drawer').then(() => {
    const prototype = customElements.get('pm-cart-drawer').prototype;
    const originalChooseGuest = prototype.chooseGuest;
    prototype.chooseGuest = async function (control) {
      if (!this.bankClubEnabled()) return originalChooseGuest.call(this, control);
      if (this.updating || control?.disabled) return false;
      this.updating = true;
      window.PK_BANK_OPTOUT_BLOCKED = true;
      if (control) control.disabled = true;
      this.status.textContent = 'Updating your cart\u2026';
      try {
        await window.PK_BANK_CART_CODE_SYNC;
        const cart = await this.fetchCart();
        const lines = (cart.items || []).filter((item) => this.isClubLine(item));
        if (lines.some((line) => typeof line.key !== 'string' || !line.key)) throw new Error('Unable to update your cart.');
        const body = { attributes: { _pk_club_choice: 'guest' }, discount: this.joinCodeList(cart, false).join(',') };
        if (lines.length) body.updates = Object.fromEntries(lines.map((line) => [line.key, 0]));
        const response = await fetch(this.cartUpdateUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const next = await response.json().catch(() => ({}));
        if (!response.ok || !Array.isArray(next.items) || next.items.some((item) => this.isClubLine(item))) {
          throw new Error('Unable to update your cart.');
        }
        this.render(next);
        window.PK_BANK_OPTOUT_BLOCKED = false;
        this.status.textContent = '';
        document.dispatchEvent(new CustomEvent('pm:cart-updated', { bubbles: true, detail: { action: 'club-guest' } }));
        return true;
      } catch (error) {
        this.status.textContent = this.errorText(error, 'Unable to update your cart.');
        return false;
      } finally {
        this.updating = false;
        if (control) control.disabled = false;
      }
    };
  });

  const portalOwnsState = () => !!document.querySelector('[data-pk-vip-portal]');

  // The Pup Club information page is for guests. Verified signed-in customers
  // go straight to management, regardless of their membership status.
  let pupClubRouted = false;
  function routeSignedInPupClub() {
    if (pupClubRouted) return true;
    if (!/^\/pages\/pup-club\/?$/.test(location.pathname)) return false;
    const club = window.PKPupClub;
    if (!club || typeof club.signedIn !== 'function' || club.signedIn() !== true) return false;
    if (typeof club.hasPendingEmailLink === 'function' && club.hasPendingEmailLink()) return false;
    pupClubRouted = true;
    location.replace('/?view=pup-club#manage');
    return true;
  }

  const authState = () => {
    const auth = window.PK_PUP_CLUB_AUTH || {};
    return {
      authenticated: auth.authenticated === true,
      activeMember: auth.authenticated === true && auth.activeMember === true,
    };
  };

  /* Card promotion. The Liquid card already rendered the standard price as the
     primary figure, so a guest, an unverified session and a failed lookup all
     need no DOM change at all. Only a confirmed active entitlement promotes. */
  const verifiedDemo = () => {
    try { return window.PKPupClub?.signedIn() === true && window.PKPupClub?.isReviewAccount() === true; } catch (_) { return false; }
  };
  function paintCards() {
    const { activeMember } = authState();
    document.querySelectorAll('[data-pk-card-price]').forEach((block) => {
      // Member prices need the Shopify sign-in too: checkout only applies the member 50% to a signed-in subscriber (Zack, 2026-09-27).
      // Pup Club in the cart shows the Pup Club price too (pk-club-cart-state.js, Zack 2026-09-27).
      const entitled = (activeMember && (window.PK_CUSTOMER?.loggedIn === true || verifiedDemo())) || (window.PK_CLUB_IN_CART === true && !activeMember && !verifiedDemo());
      const promote = entitled && Number(block.dataset.pkClubCents) > 0 &&
        Number(block.dataset.pkClubCents) < Number(block.dataset.pkPublicCents);
      if (block.classList.contains('pk-card-price--member') === promote) return;
      block.classList.toggle('pk-card-price--member', promote);
    });
  }

  function paintAccountLabels() {
    const { authenticated } = authState();
    html.classList.toggle('pk-authenticated', authenticated);
    document.querySelectorAll('[data-pk-account-label]').forEach((label) => {
      const next = authenticated ? 'Account' : 'Sign in';
      if (label.textContent !== next) label.textContent = next;
      const link = label.closest('a');
      if (link) link.setAttribute('href', authenticated ? '/?view=pup-club#account' : '/?view=pup-club#signin');
    });
    document.querySelectorAll('[data-pk-support-account]').forEach((link) => {
      const next = authenticated ? 'Manage Account' : 'Sign in';
      if (link.textContent !== next) link.textContent = next;
      const href = authenticated ? '/?view=pup-club#account' : '/?view=pup-club#signin';
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
    });
  }

  function paintManagementLinks() {
    const href = authState().authenticated ? '/?view=pup-club#account' : '/?view=pup-club#signin';
    document.querySelectorAll('[data-pk-shell-view="main"] a').forEach(link => {
      if (/^Manage (?:Account|Pup Club)$/.test(link.textContent.trim())) link.setAttribute('href', href);
    });
  }

  /* The enrollment contract is owned by pk-vip-pup-club.js and asked, never
     re-implemented, so the cart page, the drawer and the portal cannot disagree
     about whether joining is open. No club script means not ready. */
  function offerReadiness() {
    const club = window.PKPupClub;
    if (!club || typeof club.offerReadiness !== 'function') {
      return { ready: false, reasons: ['club_unavailable'] };
    }
    try { return club.offerReadiness(); } catch (_) { return { ready: false, reasons: ['club_error'] }; }
  }

  function enrollmentClosedMessage() {
    const club = window.PKPupClub;
    const fallback = 'Pup Club enrollment is not open yet. Nothing has been added, started or charged.';
    if (!club || typeof club.enrollmentClosedMessage !== 'function') return fallback;
    try { return club.enrollmentClosedMessage() || fallback; } catch (_) { return fallback; }
  }

  /* The forced-network form of the same contract, owned by pk-vip-pup-club.js.
     Asking club.refreshOffer() was not enough on its own: without the force
     flag it returns the offer this page already had, so the "fresh" check was
     answering from cache. There is no local fallback that re-fetches; a client
     that cannot offer the forced gate is treated as unavailable. */
  function freshOfferReadiness() {
    const club = window.PKPupClub;
    if (!club || typeof club.freshOfferReadiness !== 'function') {
      return Promise.resolve({ ready: false, reasons: ['club_unavailable'], fresh: false });
    }
    return Promise.resolve(club.freshOfferReadiness())
      .then((readiness) => {
        freshVerified = !!(readiness && readiness.ready === true);
        return readiness;
      })
      .catch(() => {
        freshVerified = false;
        return { ready: false, reasons: ['offer_unavailable'], fresh: false };
      });
  }

  /* Only a completed successful forced refresh may lift a server-rendered
     closed state. It starts false on every page load, so no script, a failed
     fetch, a closed contract or a disabled provider all leave the page exactly
     as the server rendered it. */
  let freshVerified = false;
  let freshPending = false;
  /* One automatic verification per page view. Without this latch the repaint
     that follows a closed verdict would ask for another one and never stop; a
     tap on an add control still forces its own refresh through
     freshOfferReadiness() immediately before it acts. */
  let freshChecked = false;
  function verifyFreshReadiness() {
    if (freshPending || freshChecked) return;
    freshPending = true;
    freshOfferReadiness().finally(() => {
      freshPending = false;
      freshChecked = true;
      paint();
    });
  }
  const freshReady = () => freshVerified && offerReadiness().ready === true;

  /* Liquid server-rendered the checkout control disabled because a Pup Club line
     is in this cart. Only a contract this page has just re-read from the server
     may lift that, so the page fails closed with no script, a failed offer
     fetch, a stale cached answer or any false clause. */
  function paintMembershipCheckoutGate() {
    /* Shopify owns the active Pup Club selling plan and checkout. A valid
       membership line therefore follows the same checkout handoff as every
       other purchasable line on the Bank storefront. */
  }

  /* Cart page Pup Club row. Liquid already proved the membership is purchasable
     and absent from this cart, so two conditions remain: entitlement, because an
     active member never sees it, and the server enrollment contract, because a
     closed contract may not invite anyone to join. Terms come from the versioned
     plan record. */
  /* An add path passes the exact response that authorized it, so the wording on
     the line it writes comes from the same read as the id the line carries. */
  function clubTerms(snapshot) {
    /* The cart page does not print the catalog data, so it falls back to the
       Shopify price Liquid wrote on the row itself. */
    const row = document.querySelector('[data-pm-cart-club-page]');
    const cents = Number(window.PK_SHOPIFY_CATALOG?.membership?.priceCents || row?.dataset.pmClubPriceCents);
    return cents > 0 ? `21-day free trial, then $${(cents / 100).toFixed(2)} every 28 days` : 'See checkout for current pricing';
  }

  function paintCartClub() {
    /* The join invitation on the cart page is part of the same offer. It stays
       hidden while the contract is closed, so no member-price claim is made,
       and it is revealed only once a forced refresh has returned a ready
       contract — never from the answer this page happened to load with. */
    const note = document.querySelector('[data-pk-club-pricing-cart]');
    const row = document.querySelector('[data-pm-cart-club-page]');
    if (note) note.hidden = true;
    if (!row) return;
    const terms = clubTerms();
    const show = !authState().activeMember && !!terms;
    if (show) {
      const slot = row.querySelector('[data-pm-cart-club-terms]');
      if (slot && slot.textContent !== terms) slot.textContent = terms;
    }
    row.hidden = !show;
  }

  /* Reaching checkout with a Pup Club line in the cart is the enrollment
     unlock, so the published offer is re-read from the server at that moment
     and the submit is replayed only when the whole contract comes back ready.
     Ordinary product carts render the other form and never reach this. */
  function bindMembershipCheckoutForm() {
    /* No special interception: Shopify receives the membership selling plan in
       the cart line and continues to its normal checkout. */
  }

  function bindCartClub() {
    const row = document.querySelector('[data-pm-cart-club-page]');
    const button = row?.querySelector('[data-pm-cart-club-add-page]');
    if (!button || button.dataset.pkBound === 'true') return;
    button.dataset.pkBound = 'true';
    button.addEventListener('click', async () => {
      const variantId = Number(row.dataset.pmClubVariant);
      if (!(variantId > 0) || button.disabled) return;
      button.disabled = true;
      const line = { id: variantId, quantity: 1 };
      const terms = clubTerms();
      line.properties = { 'Pup Club plan': terms };
      const plan = Number(row.dataset.pmClubSellingPlan);
      if (plan > 0) line.selling_plan = plan;
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: [line] })
        });
        if (!response.ok) throw new Error('add_failed');
        /* Joining on the Bank cart also applies the joining-order code (50% off
           the member catalog on this order) and clears an earlier "No savings"
           choice. If this step fails, pk-bank-cart.js applies the code on the
           next load. */
        const code = String(row.dataset.pmClubCode || '').trim();
        try {
          const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
          const cart = cartResponse.ok ? await cartResponse.json() : {};
          const body = { attributes: { _pk_club_choice: '' } };
          if (code) {
            const codes = (cart.discount_codes || []).map((entry) => entry.code)
              .filter((value) => value && value.toUpperCase() !== code.toUpperCase());
            body.discount = codes.concat(code).join(',');
          }
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(body)
          });
        } catch (_) { /* the cart page re-syncs the code on load */ }
        /* The cart page is server-rendered, so reload to show the new line and
           its totals rather than patching two sources of truth in the DOM. */
        location.assign(location.pathname);
      } catch (_) {
        button.disabled = false;
        const slot = row.querySelector('[data-pm-cart-club-terms]');
        if (slot) slot.textContent = 'We could not add Pup Club. Your cart was not changed.';
      }
    });
  }

  const paint = () => {
    if (routeSignedInPupClub()) return;
    paintCards(); paintAccountLabels(); paintManagementLinks(); paintMembershipCheckoutGate();
    paintCartClub(); bindCartClub(); bindMembershipCheckoutForm();
  };

  /* One state publisher per native page. Async answers cannot revive a session
     after sign-out, and history restoration always rechecks the same contract. */
  let publishSequence = 0;
  /* First answer of this page: 0 undecided, 1 it was a reconnecting (indeterminate)
     answer, 2 settled. A verified restore re-reads once only while it is 1. */
  let initialRead = 0;
  function clearPublished() {
    window.PK_PUP_CLUB_AUTH = { authenticated: false, activeMember: false, status: null };
    window.PK_MEMBER_STATE = null;
    try { window.dispatchEvent(new Event('pk:member-state')); } catch (_) {}
  }
  async function publish() {
    if (portalOwnsState()) return;
    const sequence = ++publishSequence;
    const club = window.PKPupClub;
    if (!club || typeof club.accountState !== 'function') {
      clearPublished();
      html.classList.remove('pk-auth-pending');
      return;
    }
    try {
      const account = await club.accountState();
      if (sequence !== publishSequence) return;
      if (initialRead < 2) initialRead = account?.authenticationIndeterminate === true ? 1 : 2;
      const authenticated = account?.authenticated === true && account?.authenticationIndeterminate !== true;
      const entitlement = account && account.entitlement;
      window.PK_PUP_CLUB_AUTH = {
        authenticated,
        activeMember: authenticated && entitlement?.benefitsActive === true,
        status: String(entitlement?.status || '').toLowerCase() || null,
      };
      window.PK_MEMBER_STATE = authenticated ? account.membership || null : null;
      if (authenticated) routeSignedInPupClub();
    } catch (error) {
      if (sequence !== publishSequence || error?.stale) return;
      initialRead = 2;
      clearPublished();
    } finally {
      if (sequence === publishSequence) {
        html.classList.remove('pk-auth-pending');
        try { window.dispatchEvent(new Event('pk:member-state')); } catch (_) {}
      }
    }
  }

  paint();
  addEventListener('pk:member-state', paint);
  let accountChanged = false;
  addEventListener('pk:auth-state', event => {
    if (portalOwnsState()) { paint(); return; }
    const reason = event?.detail?.reason;
    if (['remote-signout', 'signed-out', 'session-cleared', 'invalid-session', 'session-expired', 'account-changed'].includes(reason)) {
      accountChanged = reason === 'account-changed';
      initialRead = 2;
      publishSequence++;
      clearPublished();
      html.classList.remove('pk-auth-pending');
    } else if (reason === 'review-signin' || (accountChanged && (reason === 'session-restored' || reason === 'session-refreshed'))) {
      accountChanged = false;
      initialRead = 2;
      clearPublished();
      publish();
    } else if (initialRead === 1 && reason === 'session-restored' && event.detail.authenticated === true) {
      initialRead = 2;
      publish();
    } else paint();
  });
  addEventListener('pk:offer-state', paint);
  addEventListener('pk:club-cart', paint);
  addEventListener('pageshow', event => {
    if (!event.persisted || portalOwnsState()) return;
    freshVerified = false;
    freshChecked = false;
    clearPublished();
    publish();
  });

  /* Bank home and purifier price row, 2026-10-03 (not the filter landing): the member pill reads like the Meta row ("SAVE 50%") so strike,
     total and pill fit one line with the total centered. Display only: the percent comes from the
     two prices already in the row, rounded the same way as the Meta pill. A value that cannot be
     read leaves the original wording in place. */
  const rowAmount = (node) => {
    const value = Number(String((node && node.textContent) || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(value) && value > 0 ? value : NaN;
  };
  const shortenSavings = (row) => {
    const pill = row.querySelector('[data-pm-save]');
    if (!pill || !/^\s*You save\b/i.test(pill.textContent)) return;
    const total = rowAmount(row.querySelector('[data-pm-price]'));
    const compare = rowAmount(row.querySelector('[data-pm-compare-price]'));
    if (!(compare > total)) return;
    const percent = Math.round((1 - total / compare) * 100);
    if (percent >= 1) pill.textContent = `SAVE ${percent}%`;
  };
  const watchPriceRows = () => document.querySelectorAll('[data-pm-price-row]').forEach((row) => {
    if (row.pkSavingsWatched || row.closest('[data-pk-filter-landing]')) return;
    row.pkSavingsWatched = true;
    shortenSavings(row);
    new MutationObserver(() => shortenSavings(row)).observe(row, { childList: true, characterData: true, subtree: true });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchPriceRows, { once: true });
  } else {
    watchPriceRows();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', publish, { once: true });
  } else {
    publish();
  }
})();
